import qs from 'query-string'

import getRequest from 'utils/getRequest'
import { getApiUrl } from 'utils/apiBaseUrl'
import postRequest from 'utils/postRequest'
import uploadFileRequest from 'utils/uploadFileRequest'
import deleteRequest from 'utils/deleteRequest'
import filterUndefinedFromObject, { checkIfIntegrityVerified } from 'utils/filterUndefinedFromObject'
import patchRequest from 'utils/patchRequest'
import { editOnly } from 'routing/constants'

const path = '/api/site'
const csvPath = '/api/csv'
const reassessmentPath = '/api/reassessment/sites'
const commentsPath = '/api/comments'
const repliesPath = '/api/replies'
const visibilityPath = '/api/user/site-permissions/site'
const editSitePath = '/api/editSite/sites'

//eslint-disable-next-line
export const requestGetSites = ({
  // eslint-disable-next-line no-magic-numbers
  resultsPerPage = 12,
  // eslint-disable-next-line no-magic-numbers
  currentPage = 1,
  siteStatus = '',
  year = '',
  country = '',
  sortBy = 'name',
  sortDirection = 'ASC',
  toggle = false,
  signal
}) => {
  const queryString = {
    resultsPerPage,
    currentPage,
    siteStatus,
    year,
    country,
    sortBy,
    toggle,
    sortDirection
  }
  const queryStringParsed = qs.stringify(queryString)
  return getRequest(`${path}/?${queryStringParsed}`, signal)
}

export const requestProposeSite = () => {
  return postRequest(`${path}/`)
}

export const deleteSiteRequest = (siteId, SiteStatusId, reassessmentFlag, siteHistoryId) => {
  const url = reassessmentFlag ? `${reassessmentPath}/${siteHistoryId}/delete` : `${path}/${siteId}/supersed`
  return deleteRequest(url, { SiteStatusId })
}

export const deleteEditSiteOnlyRequest = (editOnlySiteId, SiteStatusId) => {
  const url = `${editSitePath}/${editOnlySiteId}/delete`
  return deleteRequest(url, { SiteStatusId })
}

export const requestReassesSite = (siteId) => {
  return postRequest(`${reassessmentPath}/${siteId}`)
}

export const requestGetSite = (siteId, siteHistoryId) => {
  const url = siteHistoryId ? `${reassessmentPath}/${siteHistoryId}` : `${path}/${siteId}`
  return getRequest(url)
}

export const requestGetEditOnlySite = async (editOnlySiteId) => {
  return await getRequest(`${editSitePath}/${editOnlySiteId}`)
}

/**
 * Creates or replaces an existing draft KBA geometry
 * @param {Number} siteId - the site id for the draft KBA
 * @param {Number} siteHistoryId - the site history id for a site being reassessed
 * @param {object} geometry - the polygon geometry of the draft KBA
 * @param {object} attributes - the attributes to add to the feature on the draft layer
 * @returns {Promise}
 */
export const postDraftSiteGeometry = (siteId, siteHistoryId, geometry, attributes) => {
  const url = siteHistoryId ? `${reassessmentPath}/${siteHistoryId}/geometry` : `${path}/${siteId}/geometry`
  return postRequest(url, { geometry, attributes })
}

/**
 * Deletes the site geometry for an existing draft KBA
 * @param {string} siteId
 * @param {Number} siteHistoryId - the site history id for a site being reassessed
 * @returns {Promise}
 */
export const deleteDraftSiteGeometry = (siteId, siteHistoryId) => {
  const url = siteHistoryId ? `${reassessmentPath}/${siteHistoryId}/delete/geometry` : `${path}/${siteId}/delete/geometry`
  return deleteRequest(url)
}

/**
 * Gets the geometry for a current KBA
 * @param {string} siteId
 * @returns {Promise}
 */
export const requestGetCurrentSiteGeometry = (siteId, siteHistoryId) => {
  const url = siteHistoryId ? `${reassessmentPath}/${siteHistoryId}/geometry` : `${path}/${siteId}/geometry`
  return getRequest(url)
}

/**
 * Gets the geometry for a draft KBA
 * @param {string} siteId
 * @returns {Promise}
 */
export const requestGetDraftSiteGeometry = (siteId, siteHistoryId) => {
  const url = siteHistoryId ? `${reassessmentPath}/${siteHistoryId}/draftGeometry` : `${path}/${siteId}/draftGeometry`
  return getRequest(url)
}

//eslint-disable-next-line
export const requestGetSitesSearch = ({
  // eslint-disable-next-line no-magic-numbers
  resultsPerPage = 12,
  // eslint-disable-next-line no-magic-numbers
  currentPage = 1,
  siteStatus = '',
  year = '',
  country = '',
  sortBy = 'name',
  query = '',
  sortDirection = 'ASC',
  toggle = false,
  signal
}) => {
  const queryString = {
    resultsPerPage,
    currentPage,
    siteStatus,
    year,
    country,
    sortBy,
    toggle,
    sortDirection,
    query
  }

  const queryStringParsed = qs.stringify(queryString)
  return getRequest(`${path}/search?${queryStringParsed}`, signal)
}

export const requestGetPotentialCoProposers = ({ searchQuery = '', excludeIds = [] }, signal) => {
  return postRequest(`${path}/co-proposer`, { searchQuery, excludeIds }, signal)
}

export const requestRemoveCoProposerFromSite = ({ siteId, proposerId }, siteHistoryId) => {
  const url = siteHistoryId
    ? `${reassessmentPath}/${siteHistoryId}/co-proposers/${proposerId}`
    : `${path}/${siteId}/co-proposer/${proposerId}`
  return deleteRequest(url)
}

export const requestGetSiteCoProposers = (siteId, siteHistoryId) => {
  const url = siteHistoryId ? `${reassessmentPath}/${siteHistoryId}/co-proposers` : `${path}/${siteId}/co-proposer`
  return getRequest(url)
}

export const requestAddNewCoProposer = ({ siteId, newProposers }, siteHistoryId) => {
  const url = siteHistoryId ? `${reassessmentPath}/${siteHistoryId}/co-proposers` : `${path}/${siteId}/co-proposer`
  return postRequest(url, { newProposers })
}

export const requestAllSitesReassessments = (siteId) => {
  return getRequest(`${path}/${siteId}/reassessments`)
}



export const requestCalculationData = (
  siteId,
  siteHistoryId,
  {
    // eslint-disable-next-line no-magic-numbers
    resultsPerPage = 8,
    // eslint-disable-next-line no-magic-numbers
    currentPage = 1
  }
) => {
  const queryString = {
    resultsPerPage,
    currentPage
  }
  const queryStringParsed = qs.stringify(queryString)
  if (siteHistoryId) {
    return getRequest(`${reassessmentPath}/${siteHistoryId}/kba-criterias?${queryStringParsed}`)
  }
  return getRequest(`${path}/${siteId}/kba-criterias?${queryStringParsed}`)
}

export const requestEcoSystemCalculator = (siteId, siteHistoryId) => {
  const url = siteHistoryId
    ? `${reassessmentPath}/${siteHistoryId}/eco-systems/Kba-criterias`
    : `/api/eco-system/site/${siteId}/kba-criterias`

  return getRequest(url)
}

export const requestTreatmentData = (siteId, siteHistoryId) => {
  const url = siteHistoryId ? `${reassessmentPath}/${siteHistoryId}/treatment` : `${path}/${siteId}/treatment`
  return getRequest(url)
}

// requests for comments section

export const removeCommentById = async(siteId, commentId, siteHistoryId, path = null) => {
  const url = path === editOnly ? `${editSitePath}/${siteId}/comments/${commentId}` : siteHistoryId ? `${reassessmentPath}/${siteHistoryId}/comments/${commentId}` : `${commentsPath}/${commentId}/site/${siteId}`
  return deleteRequest(url)
}

export const requestCommentsData = async (siteId, siteHistoryId, path=null) => {
  const url = path === editOnly ? `${editSitePath}/${siteId}/comments` : siteHistoryId ? `${reassessmentPath}/${siteHistoryId}/comments` : `${commentsPath}/site/${siteId}`
  return await getRequest(url)
}

export const requestAddCommentData = async (siteId, siteHistoryId, data, path=null) => {
  const url = path === editOnly ? `${editSitePath}/${siteId}/comments` : siteHistoryId ? `${reassessmentPath}/${siteHistoryId}/comments` : `${commentsPath}/site/${siteId}`
  return await postRequest(url, data)
}

export const updateCommentById = (siteId, commentId, siteHistoryId, data, path = null) => {
  const url = path === editOnly ? `${editSitePath}/${siteId}/comments/${commentId}` : siteHistoryId ? `${reassessmentPath}/${siteHistoryId}/comments/${commentId}` : `${commentsPath}/${commentId}/site/${siteId}`
  return patchRequest(url, data)
}

// requests for replies section

export const updateReplyById = (siteId, replyId, siteHistoryId, data, path = null) => {
  const url = path === editOnly ? `${editSitePath}/${siteId}/replies/${replyId}` : siteHistoryId ? `${reassessmentPath}/${siteHistoryId}/replies/${replyId}` : `${repliesPath}/${replyId}/site/${siteId}`
  return patchRequest(url, data)
}

export const requestAddReplyData = async (siteId, commentId, siteHistoryId, data, path = null) => {
  const url = path === editOnly ? `${editSitePath}/${siteId}/comments/${commentId}/replies` : siteHistoryId ? `${reassessmentPath}/${siteHistoryId}/comments/${commentId}/replies` : `${repliesPath}/site/${siteId}/comments/${commentId}`
  return await postRequest(url, data)
}

export const requestAddSubReplyData = (siteId, commentId, data) => {
  return postRequest(`${repliesPath}/site/${siteId}/comments/${commentId}`, data)
} // not yet used

export const requestGetReplyCopy = async (siteId, commentId, siteHistoryId, data, path=null) => {
  const url = path === editOnly ? `${editSitePath}/${siteId}/comments/${commentId}/replies` : siteHistoryId ? `${reassessmentPath}/${siteHistoryId}/comments/${commentId}/replies` : `${repliesPath}/site/${siteId}/comments/${commentId}`
  return await getRequest(url, data)
}

export const removeReplyCopy2 = async(siteId, replyId, siteHistoryId, data, path=null) => {
  const url = path === editOnly ? `${editSitePath}/${siteId}/replies/${replyId}` : siteHistoryId ? `${reassessmentPath}/${siteHistoryId}/replies/${replyId}` : `${repliesPath}/${replyId}/site/${siteId}`
  return deleteRequest(url, data)
}

export const resolveCommentById = async (siteId, commentId, siteHistoryId, path = null) => {
  const url = path === editOnly ? `${editSitePath}/${siteId}/comments/${commentId}/resolve` : siteHistoryId ? `${reassessmentPath}/${siteHistoryId}/comments/${commentId}/resolve` : `${commentsPath}/${commentId}/site/${siteId}/resolve`;
  return await patchRequest(url);
};

export const reactivateCommentById = (siteId, commentId, siteHistoryId, path=null) => {
  const url = path === editOnly ? `${editSitePath}/${siteId}/comments/${commentId}/reactivate` : siteHistoryId ? `${reassessmentPath}/${siteHistoryId}/comments/${commentId}/reactivate` : `${commentsPath}/${commentId}/site/${siteId}/reactivate`;
  return patchRequest(url);
};

// Submits the sensitive species visibility request. Backend should persist this as
// sensitive_species_visibility_request (snake_case) on SitesHistories / Sites.
//
// IMPORTANT:
// We intentionally do **not** use the shared `postRequest` helper here because that
// helper auto-follows any `{ redirect: "/some-path" }` responses by calling
// `window.location.href = ...`. The sensitive-species acknowledgment endpoint may
// return such a shape for legacy/compat reasons, and following it can cause a
// full-page reload / white screen in some environments.
//
// Instead, we:
// - Call the endpoint directly with `fetch`
// - Safely parse JSON (or fall back to `{}`) without following any `redirect` keys
// - Always return a plain object so callers can safely inspect flags like
//   `alreadySubmitted` / `already_submitted` / `message`.
export const requestSubmitSensitiveSpeciesAcknowledgment = async (siteId, siteHistoryId) => {
  const url = siteHistoryId
    ? `${reassessmentPath}/${siteHistoryId}/sensitive-species-acknowledgment`
    : `${path}/${siteId}/sensitive-species-acknowledgment`

  const fullUrl = getApiUrl(url)

  try {
    const response = await fetch(fullUrl, {
      method: 'POST',
      mode: 'cors',
      cache: 'no-cache',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      redirect: 'follow',
      referrerPolicy: 'no-referrer'
    })

    let json = null
    try {
      json = await response.json()
    } catch {
      // If there's no JSON body (e.g. 204), fall through to `{}`
    }

    return json || {}
  } catch {
    return {}
  }
}
export const requestUpdateSiteFiles = async (siteId, siteHistoryId, formData, options = {}) => {
  const url = siteHistoryId ? `${reassessmentPath}/${siteHistoryId}` : `${path}/${siteId}`
  
  // Always use XMLHttpRequest for file uploads to handle FormData properly
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    
    // Set up progress tracking
    if (options.onProgress) {
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          options.onProgress(event.loaded, event.total)
        }
      })
    }
    
    xhr.addEventListener('load', () => {
      
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const response = JSON.parse(xhr.responseText)
          resolve(response)
        } catch (e) {
          resolve({})
        }
      } else {
        reject(new Error(`HTTP ${xhr.status}: ${xhr.statusText}`))
      }
    })
    
    xhr.addEventListener('error', () => {
      reject(new Error('Network error'))
    })
    
    xhr.addEventListener('abort', () => {
      reject(new Error('Upload aborted'))
    })
    
    xhr.open(siteHistoryId ? 'PATCH' : 'POST', url)
    xhr.setRequestHeader('Accept', 'application/json')
    
    // Use same-origin credentials for authentication (like other API calls)
    xhr.withCredentials = true
    
    // Don't set Content-Type for FormData - let browser set it with boundary
    xhr.send(formData)
  })
}

export const requestUploadAboutPageDocs = async (siteId, formData) => {
  const response = await fetch(getApiUrl(`/api/pages/about/${siteId}/upload-about-docs`), {
    method: 'POST',
    body: formData,
    headers: {
      'Accept': 'application/json'
    }
  })
  return response.json()
}


export const requestUploadSpeciesDocs = async (siteId, formData) => {
  const response = await fetch(getApiUrl(`/api/site-specie/${siteId}/upload-species-docs`), {
    method: 'POST',
    body: formData,
    headers: {
      'Accept': 'application/json'
    }
  })
  return response.json()
}


export const requestDeleteSiteFiles = async (siteId, siteHistoryId, fileId) => {
  const url = siteHistoryId ? `${reassessmentPath}/${siteHistoryId}/deleteSiteFiles/${fileId}` : `${path}/${siteId}/deleteSiteFiles/${fileId}`
  const response = await fetch(url, {
    method: 'DELETE',
    headers: {
      'Accept': 'application/json'
    }
  })
  return response.json()

}

export const requestUpdateSite = async(siteId, siteHistoryId, {
  name,
  threatenedBiodiversity,
  geographicallyRestrictedBiodiversity,
  ecologicalIntegrity,
  biologicalProcesses,
  irreplaceabilityThroughQuantitiveAnalysis,
  reviewersContactsThatCanComment,
  ongoingConservationActions,
  conservationActionsNeeded,
  siteNameNational,
  state,
  siteArea,
  siteAreaCalculated,
  latitudeOfMidPoint,
  longitudeOfMidPoint,
  lowestAltitude,
  highestAltitude,
  rationaleForSiteInformation,
  siteDescription,
  howIsTheSiteManaged,
  deliniationRationale,
  coveredByWhatProtectedAreas,
  additionalBiodiversityValues,
  indigenousGroups,
  landUseRegimesAtSite,
  notes,
  references,
  calculatedProtectedArea,
  CoverageOfProtectedAreasId,
  AltitudeOfBathymetryId,
  EcoSystemsId,
  EcoSystems2LargestId,
  EcoSystems3LargestId,
  EcoSystemsSmallestId,
  SiteDetailsCountryId,
  EcologicalIntegrityEcoRegionId,
  numberOfCriterionSitesInEcoRegion,
  evidenceOfLowHumanImpact,
  evidenceForIntactEcologicalCommunities,
  dateOfAssesment,
  assessmentDate,
  overlapOECMProtectedCoverage,
  proposalPurpose,
  proposalDetails,
  consultationWithBiodiversityKnowledgeHolders,
  consultationWithPreviousProposers,
  consultationWithGovernment,
  consultationWithCustomaryLegalRightsHolders,
  consultationWithContributors,
  triggerReasonId,
  triggerReasonText,
  threatsDescription,
  minDepth,
  maxDepth,
  supersededSitesDesription,
  calculatedOECMArea,
  habitatDescription,
  irreplaceabilityScore,
  irreplaceabilityAssessmentApproved,
  contributors_assessment,
}) => {

  const preBody = {
    name,
    threatenedBiodiversity,
    geographicallyRestrictedBiodiversity,
    ecologicalIntegrity,
    biologicalProcesses,
    irreplaceabilityThroughQuantitiveAnalysis,
    reviewersContactsThatCanComment,
    ongoingConservationActions,
    conservationActionsNeeded,
    siteNameNational,
    state,
    siteArea,
    siteAreaCalculated,
    latitudeOfMidPoint,
    longitudeOfMidPoint,
    lowestAltitude,
    highestAltitude,
    rationaleForSiteInformation,
    siteDescription,
    howIsTheSiteManaged,
    deliniationRationale,
    coveredByWhatProtectedAreas,
    additionalBiodiversityValues,
    indigenousGroups,
    landUseRegimesAtSite,
    notes,
    references,
    calculatedProtectedArea,
    CoverageOfProtectedAreasId,
    AltitudeOfBathymetryId,
    EcoSystemsId,
    EcoSystems2LargestId,
    EcoSystems3LargestId,
    EcoSystemsSmallestId,
    SiteDetailsCountryId,
    EcologicalIntegrityEcoRegionId,
    numberOfCriterionSitesInEcoRegion,
    evidenceOfLowHumanImpact,
    evidenceForIntactEcologicalCommunities,
    dateOfAssesment,
    assessmentDate,
    overlapOECMProtectedCoverage,
    proposalPurpose,
    proposalDetails,
    consultationWithBiodiversityKnowledgeHolders,
    consultationWithPreviousProposers,
    consultationWithGovernment,
    consultationWithCustomaryLegalRightsHolders,
    consultationWithContributors,
    triggerReasonId,
    triggerReasonText,
    threatsDescription,
    minDepth,
    maxDepth,
    supersededSitesDesription,
    calculatedOECMArea,
    habitatDescription,
    irreplaceabilityScore,
    irreplaceabilityAssessmentApproved,
    contributors_assessment,
  }
  const requestBody = filterUndefinedFromObject(preBody)


  const isIntegrityC = checkIfIntegrityVerified(requestBody)
  if (isIntegrityC) {
    requestBody.ecologicalIntegrity = true
  } else {
    requestBody.ecologicalIntegrity = false
  }
  if (siteHistoryId) {
    return await patchRequest(`${reassessmentPath}/${siteHistoryId}`, requestBody)
  }
  return await postRequest(`${path}/${siteId}`, requestBody)
}

export const requestUploadGISFile = (siteId, formDataWithFile) => {
  return uploadFileRequest(`${path}/${siteId}/upload-gis-file`, formDataWithFile)
}

export const requestFinishProposeSite = (siteId, siteHistoryId) => {
  const url = siteHistoryId ? `${reassessmentPath}/${siteHistoryId}/finish-propose` : `${path}/${siteId}/finish-propose`
  return postRequest(url)
}

export const requestValidateSite = (siteId, siteHistoryId) => {
  const url = siteHistoryId ? `${reassessmentPath}/${siteHistoryId}/validate` : `${path}/${siteId}/validate`
  return postRequest(url)
}

export const requestValidateEditOnlySite = (siteHistoryId) => {
  const url =  `${editSitePath}/${siteHistoryId}/validate`
  return postRequest(url)
}

export const requestValidateSiteGeometry = (siteId, siteHistoryId) => {
  const url = siteHistoryId ? `${reassessmentPath}/${siteHistoryId}/validateGeometry` : `${path}/${siteId}/validateGeometry`
  return postRequest(url)
}

export const requestSupersedSite = (siteId, supersedComment) => {
  return postRequest(`${path}/${siteId}/supersed`, { supersedComment })
}

export const requestUndoSupersedSite = (siteId) => {
  return postRequest(`${path}/${siteId}/undo-supersed`)
}

export const requestReviewByRfp = (siteId, siteHistoryId, isApproved) => {
  const url = siteHistoryId ? `${reassessmentPath}/${siteHistoryId}/review-by-rfp` : `${path}/${siteId}/review-by-rfp`

  return postRequest(url, isApproved)
}

export const requestNominateSite = (siteId, siteHistoryId) => {
  const url = siteHistoryId ? `${reassessmentPath}/${siteHistoryId}/nominate` : `${path}/${siteId}/nominate`

  return postRequest(url)
}

export const requestConfirmState = async (siteId, siteHistoryId, isApproved) => {
  const url = siteHistoryId ? `${reassessmentPath}/${siteHistoryId}/confirm` : `${path}/${siteId}/confirm`

  return await postRequest(url, isApproved)
}

export const requestApproveEdits = async(siteId, editOnlySiteId, isApproved) => {
  const url = `${editSitePath}/${siteId}/approve-edits/${editOnlySiteId}`
  return await postRequest(url, isApproved)
}

export const requestDelistState = (siteHistoryId, isApproved) => {
  return postRequest(`${reassessmentPath}/${siteHistoryId}/delist`, { isApproved: isApproved })
}

export const requestDoesNotQualify = (siteId) => {
  return postRequest(`${path}/${siteId}/does-not-qualify`)
}

export const requestSiteVisibility = (siteId) => {
  return getRequest(`${visibilityPath}/${siteId}`)
}

export const requestValidateNewImportData = (data) => {
  return uploadFileRequest(`${csvPath}/upload/validate`, data)
}

export const requestImportNewData = (data) => {
  return uploadFileRequest(`${csvPath}/upload/update`, data)
}
export const requestBulkImportTemplate = () => {
  return getRequest(`bulkImportTemplate.zip`)
}

export const requestGetSiteAttributesTemplate = () => {
  return getRequest(`${csvPath}/forms/sites`)
}

export const requestGetSiteThreatsTemplate = () => {
  return getRequest(`${csvPath}/forms/threats`)
}

export const requestGetSiteActionsTemplate = () => {
  return getRequest(`${csvPath}/forms/actions`)
}

// Add deleteCriterionC here
export const deleteCriterionC = (siteId, siteHistoryId) => {
  const url = siteHistoryId
    ? `${reassessmentPath}/${siteHistoryId}/criterionC`
    : `${path}/${siteId}/criterionC`;
  return deleteRequest(url);
};

export const requestEditSite = (siteId) => {
  return postRequest(`${editSitePath}/${siteId}/edit`)
}

export const requestConfirmEditOnlyState = (siteId, siteHistoryId, isApproved) => {
  const url = siteHistoryId
    ? `${editSitePath}/${siteHistoryId}/confirm`
    : `${editSitePath}/${siteId}/confirm`;

  return postRequest(url, { isApproved });
};

export const requestFinishEditOnlySite = (siteHistoryId) => {
  const url = `${editSitePath}/${siteHistoryId}/finishEditOnlyEdits`

  return postRequest(url);
};

export const requestUpdateEditOnlySite = (siteId, data) => {
  return patchRequest(`${editSitePath}/${siteId}`, data);
};

export const requestUpdateSpeciesFiles = async (siteId, siteHistoryId, formData, options = {}) => {
  const url = siteHistoryId
    ? `${reassessmentPath}/${siteHistoryId}/species-files`
    : `/api/site-specie/${siteId}/species-files`
  
  // Always use XMLHttpRequest for file uploads to handle FormData properly
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    
    // Set up progress tracking
    if (options.onProgress) {
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          options.onProgress(event.loaded, event.total)
        }
      })
    }
    
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const response = JSON.parse(xhr.responseText)
          resolve(response)
        } catch (e) {
          console.error('Failed to parse response:', e)
          resolve({})
        }
      } else {
        console.error(`Upload failed: HTTP ${xhr.status}: ${xhr.statusText}`)
        reject(new Error(`HTTP ${xhr.status}: ${xhr.statusText}`))
      }
    })
    
    xhr.addEventListener('error', () => {
      console.error('Upload network error')
      reject(new Error('Network error'))
    })
    
    xhr.addEventListener('abort', () => {
      console.error('Upload aborted')
      reject(new Error('Upload aborted'))
    })
    
    xhr.open(siteHistoryId ? 'PATCH' : 'POST', url)
    xhr.setRequestHeader('Accept', 'application/json')
    
    // Use same-origin credentials for authentication (like other API calls)
    xhr.withCredentials = true
    
    // Don't set Content-Type for FormData - let browser set it with boundary
    xhr.send(formData)
  })
}


// --- Corrected requestDeleteSpeciesFiles function ---
export const requestDeleteSpeciesFiles = async (siteId, siteHistoryId, fileId) => {
  const url = siteHistoryId
    // If siteHistoryId exists, use reassessment path and append fileId to 'species-files'
    ? `${reassessmentPath}/${siteHistoryId}/species-files/${fileId}`
    // Otherwise, use the base path and append fileId to 'species-files'
    : `/api/site-specie/${siteId}/species-files/${fileId}` // Corrected path: removed 'deleteSpeciesFiles' and added fileId to 'species-files'

  const response = await fetch(url, {
    method: 'DELETE',
    headers: {
      'Accept': 'application/json'
    }
  })

  return response.json()
}

// api/site.js (or wherever requestGetSpeciesFiles lives)
export const requestGetSpeciesFiles = async (siteId, siteHistoryId) => {
  // Handle both object and primitive formats for backward compatibility
  let actualSiteId = siteId
  let actualSiteHistoryId = siteHistoryId

  // If first param is an object, extract values from it
  if (siteId && typeof siteId === 'object' && !Array.isArray(siteId)) {
    actualSiteId = siteId.id || siteId.siteId || siteId.SiteId
    actualSiteHistoryId = siteId.siteHistoryId || siteId.sitehistoryId || siteId.historyId || siteHistoryId
  }

  if (!actualSiteId) {
    throw new Error("requestGetSpeciesFiles: siteId is required");
  }

  // Ensure siteId is a primitive value
  const primitiveSiteId = typeof actualSiteId === 'object' ? (actualSiteId?.id || actualSiteId?.siteId) : actualSiteId
  const primitiveHistoryId = actualSiteHistoryId && typeof actualSiteHistoryId === 'object' 
    ? (actualSiteHistoryId?.siteHistoryId || actualSiteHistoryId?.historyId) 
    : actualSiteHistoryId

  const qs = primitiveHistoryId ? `?siteHistoryId=${encodeURIComponent(primitiveHistoryId)}` : "";
  const url = `/api/site-specie/${primitiveSiteId}/species-files${qs}`;

  try {
    const response = await fetch(url, {
      method: "GET",
      headers: { Accept: "application/json" },
    });

    if (response.status === 404) {
      console.warn("[API][species-files][GET:missing-endpoint]", url);
      return { files: [] };
    }

    let data = null;
    try {
      data = await response.json();
    } catch (e) {
      console.warn("[API][species-files][GET:json-parse-failed]", e);
    }

    if (!response.ok) {
      console.warn("[API][species-files][GET:failed]", response.status, response.statusText);
      return { files: [] };
    }

    return data ?? { files: [] };
  } catch (error) {
    console.warn("[API][species-files][GET:error]", error);
    return { files: [] };
  }
};

// Criterion C files API functions
export const requestUpdateCriterionCFiles = async (siteId, siteHistoryId, formData, options = {}) => {
  const url = siteHistoryId
    ? `${reassessmentPath}/${siteHistoryId}/criterion-c-files`
    : `/api/criterion-c/${siteId}/criterion-c-files`
  
  // Always use XMLHttpRequest for file uploads to handle FormData properly
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    
    // Set up progress tracking
    if (options.onProgress) {
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          options.onProgress(event.loaded, event.total)
        }
      })
    }
    
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const response = JSON.parse(xhr.responseText)
          resolve(response)
        } catch (e) {
          console.error('Failed to parse response:', e)
          resolve({})
        }
      } else {
        console.error(`Upload failed: HTTP ${xhr.status}: ${xhr.statusText}`)
        reject(new Error(`HTTP ${xhr.status}: ${xhr.statusText}`))
      }
    })
    
    xhr.addEventListener('error', () => {
      console.error('Upload network error')
      reject(new Error('Network error'))
    })
    
    xhr.addEventListener('abort', () => {
      console.error('Upload aborted')
      reject(new Error('Upload aborted'))
    })
    
    xhr.open(siteHistoryId ? 'PATCH' : 'POST', url)
    xhr.setRequestHeader('Accept', 'application/json')
    
    // Use same-origin credentials for authentication (like other API calls)
    xhr.withCredentials = true
    
    // Don't set Content-Type for FormData - let browser set it with boundary
    xhr.send(formData)
  })
}

export const requestDeleteCriterionCFiles = async (siteId, siteHistoryId, fileId) => {
  const url = siteHistoryId
    ? `${reassessmentPath}/${siteHistoryId}/criterion-c-files/${fileId}`
    : `/api/criterion-c/${siteId}/criterion-c-files/${fileId}`

  const response = await fetch(url, {
    method: 'DELETE',
    headers: {
      'Accept': 'application/json'
    },
    credentials: 'include'
  })

  if (!response.ok) {
    throw new Error(`Failed to delete criterion C file (${response.status})`)
  }

  return response.json()
}

export const requestGetCriterionCFiles = async (siteId, siteHistoryId) => {
  if (!siteId) {
    throw new Error("requestGetCriterionCFiles: siteId is required");
  }

  const qs = siteHistoryId ? `?siteHistoryId=${encodeURIComponent(siteHistoryId)}` : "";
  const url = `/api/criterion-c/${siteId}/criterion-c-files${qs}`;

  const response = await fetch(url, {
    method: "GET",
    headers: { Accept: "application/json" },
    credentials: 'include'
  });

  let data = null;
  try {
    data = await response.json();
  } catch (e) {
    console.warn("[API][criterion-c-files][GET:json-parse-failed]", e);
  }

  if (!response.ok) {
    throw new Error(`Failed to fetch criterion C files (${response.status})`);
  }

  return data ?? { files: [] };
};

// Get site files (for about page)
export const requestGetSiteFiles = async (siteId, siteHistoryId) => {
  if (!siteId) {
    throw new Error("requestGetSiteFiles: siteId is required");
  }

  const qs = siteHistoryId ? `?siteId=${encodeURIComponent(siteId)}` : "";
  const url = siteHistoryId 
    ? `${reassessmentPath}/${siteHistoryId}/files${qs}`
    : `/api/site/${siteId}/files${qs}`;

  const response = await fetch(url, {
    method: "GET",
    headers: { Accept: "application/json" },
  });

  let data = null;
  try {
    data = await response.json();
  } catch (e) {
    console.warn("[API][site-files][GET:json-parse-failed]", e);
  }

  if (!response.ok) {
    throw new Error(`Failed to fetch site files (${response.status})`);
  }

  return data ?? { files: [] };
};




